import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  AiImageArchiveOutputItem,
  DamSearchAssetResult,
  ProductReferenceLookupResult,
} from '../types';
import { fetchAiImageArchiveOutputs } from '../aiImageStudioApi';
import { getAiImageAssetUrl } from '../utils';
import { ICategory } from '../../../types/damTypes';

type PickerTab = 'upload' | 'dam' | 'sku' | 'archive';

interface BaseImagePickerProps {
  open: boolean;
  categories: ICategory[];
  onClose: () => void;
  onSelectUpload: (file: File) => void;
  onSelectDamAsset: (asset: DamSearchAssetResult) => void;
  onSelectSkuAsset: (asset: DamSearchAssetResult, sku: string) => void;
  onSelectArchiveOutput: (item: AiImageArchiveOutputItem) => void;
  onSearchDamAssets: (query: string, categoryId?: string) => Promise<DamSearchAssetResult[]>;
  onLookupSkuReference: (sku: string) => Promise<ProductReferenceLookupResult>;
}

const ARCHIVE_PAGE_SIZE = 60;

const BaseImagePicker: React.FC<BaseImagePickerProps> = ({
  open,
  categories,
  onClose,
  onSelectUpload,
  onSelectDamAsset,
  onSelectSkuAsset,
  onSelectArchiveOutput,
  onSearchDamAssets,
  onLookupSkuReference,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const damDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skuDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const archiveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const archiveRequestRef = useRef(0);
  const [activeTab, setActiveTab] = useState<PickerTab>('dam');
  const [damQuery, setDamQuery] = useState('');
  const [damResults, setDamResults] = useState<DamSearchAssetResult[]>([]);
  const [damLoading, setDamLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [skuQuery, setSkuQuery] = useState('');
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuResult, setSkuResult] = useState<ProductReferenceLookupResult | null>(null);
  const [archiveQuery, setArchiveQuery] = useState('');
  const [archiveItems, setArchiveItems] = useState<AiImageArchiveOutputItem[]>([]);
  const [archiveCursor, setArchiveCursor] = useState<string | null>(null);
  const [archiveHasMore, setArchiveHasMore] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveError, setArchiveError] = useState('');

  const mainCategories = useMemo(
    () => categories.filter((category) => !category.parentCategory),
    [categories]
  );

  const searchDam = useCallback(async (query: string, categoryId?: string | null) => {
    if (query.trim().length < 2 && !categoryId) {
      setDamResults([]);
      return;
    }
    setDamLoading(true);
    try {
      const results = await onSearchDamAssets(query.trim(), categoryId || undefined);
      setDamResults(results);
    } finally {
      setDamLoading(false);
    }
  }, [onSearchDamAssets]);

  const lookupSku = useCallback(async (sku: string) => {
    if (!sku.trim()) {
      setSkuResult(null);
      return;
    }
    setSkuLoading(true);
    try {
      setSkuResult(await onLookupSkuReference(sku.trim()));
    } finally {
      setSkuLoading(false);
    }
  }, [onLookupSkuReference]);

  const loadArchive = useCallback(async (search: string, append: boolean, cursor: string | null) => {
    const requestId = archiveRequestRef.current + 1;
    archiveRequestRef.current = requestId;
    setArchiveLoading(true);
    setArchiveError('');
    try {
      const page = await fetchAiImageArchiveOutputs({
        search: search.trim() || undefined,
        cursor: append ? cursor || undefined : undefined,
        limit: ARCHIVE_PAGE_SIZE,
      });
      if (requestId !== archiveRequestRef.current) return;
      setArchiveItems((prev) => (append ? [...prev, ...page.items] : page.items));
      setArchiveCursor(page.nextCursor);
      setArchiveHasMore(page.hasMore);
    } catch (error: any) {
      if (requestId !== archiveRequestRef.current) return;
      setArchiveError(error?.response?.data?.message || error?.message || 'Kunde inte hämta arkivet.');
    } finally {
      if (requestId === archiveRequestRef.current) setArchiveLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || activeTab !== 'dam') return undefined;
    if (damDebounceRef.current) clearTimeout(damDebounceRef.current);
    if (damQuery.trim().length < 2 && !selectedCategoryId) {
      setDamResults([]);
      return undefined;
    }
    damDebounceRef.current = setTimeout(() => { void searchDam(damQuery, selectedCategoryId); }, 400);
    return () => { if (damDebounceRef.current) clearTimeout(damDebounceRef.current); };
  }, [activeTab, damQuery, open, searchDam, selectedCategoryId]);

  useEffect(() => {
    if (!open || activeTab !== 'sku') return undefined;
    if (skuDebounceRef.current) clearTimeout(skuDebounceRef.current);
    if (!skuQuery.trim()) {
      setSkuResult(null);
      return undefined;
    }
    skuDebounceRef.current = setTimeout(() => { void lookupSku(skuQuery); }, 500);
    return () => { if (skuDebounceRef.current) clearTimeout(skuDebounceRef.current); };
  }, [activeTab, lookupSku, open, skuQuery]);

  useEffect(() => {
    if (!open || activeTab !== 'archive') return undefined;
    if (archiveDebounceRef.current) clearTimeout(archiveDebounceRef.current);
    archiveDebounceRef.current = setTimeout(() => { void loadArchive(archiveQuery, false, null); }, 300);
    return () => { if (archiveDebounceRef.current) clearTimeout(archiveDebounceRef.current); };
  }, [activeTab, archiveQuery, loadArchive, open]);

  const chooseDam = (asset: DamSearchAssetResult) => {
    onSelectDamAsset(asset);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Välj basbild
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ladda upp, sök i Athena eller hämta en bild från arkivet.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value: PickerTab) => setActiveTab(value)}
          sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' } }}
        >
          <Tab value="upload" label="Ladda upp" />
          <Tab value="dam" label="Sök i Athena" />
          <Tab value="sku" label="Artikelnummer" />
          <Tab value="archive" label="Arkiv" />
        </Tabs>
      </Box>

      <Box sx={{ px: 3, py: 2.5, minHeight: 320 }}>
        {activeTab === 'upload' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              py: 6,
              border: '2px dashed',
              borderColor: 'rgba(0,0,0,0.1)',
              borderRadius: 3,
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(25,118,210,0.03)' },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Klicka för att välja en basbild från datorn
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              JPG, PNG, WebP
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                onSelectUpload(file);
                onClose();
              }}
            />
          </Box>
        )}

        {activeTab === 'dam' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Börja skriva för att söka i Athena..."
              value={damQuery}
              onChange={(event) => setDamQuery(event.target.value)}
              autoFocus
              slotProps={{ input: { endAdornment: damLoading ? <CircularProgress size={18} /> : null } }}
            />
            {mainCategories.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {mainCategories.map((category) => (
                  <Chip
                    key={category._id}
                    label={category.name}
                    size="small"
                    variant={selectedCategoryId === category._id ? 'filled' : 'outlined'}
                    color={selectedCategoryId === category._id ? 'primary' : 'default'}
                    onClick={() => setSelectedCategoryId((prev) => (prev === category._id ? null : category._id))}
                  />
                ))}
              </Box>
            )}
            {damResults.length > 0 && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1, maxHeight: 340, overflowY: 'auto' }}>
                {damResults.map((asset) => (
                  <Box
                    key={asset._id}
                    role="button"
                    tabIndex={0}
                    aria-label={`Välj ${asset.title || asset.fileName || 'bild'}`}
                    onClick={() => chooseDam(asset)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        chooseDam(asset);
                      }
                    }}
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      position: 'relative',
                      border: '2px solid transparent',
                      '&:hover .base-overlay, &:focus-within .base-overlay, &:focus-visible .base-overlay': { opacity: 1 },
                      '&:focus-visible': { borderColor: 'primary.main', outline: 'none' },
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      src={getAiImageAssetUrl(asset.thumbnailUrl || asset.previewUrl)}
                      alt={asset.title || asset.fileName}
                      sx={{ width: '100%', height: 100, bgcolor: 'grey.100', borderRadius: 2 }}
                    />
                    <Box className="base-overlay" sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.45)', opacity: 0, transition: 'opacity 0.15s' }}>
                      <AddCircleOutlineIcon sx={{ color: '#fff', fontSize: 32 }} />
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }} noWrap>
                      {asset.title || asset.fileName}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {!damLoading && (damQuery.trim().length >= 2 || selectedCategoryId) && damResults.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                Inga bilder hittades i Athena.
              </Typography>
            )}
            {damQuery.trim().length < 2 && !selectedCategoryId && (
              <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                Skriv minst 2 tecken eller välj en kategori.
              </Typography>
            )}
          </Box>
        )}

        {activeTab === 'sku' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ange artikelnummer..."
              value={skuQuery}
              onChange={(event) => setSkuQuery(event.target.value)}
              autoFocus
              slotProps={{ input: { endAdornment: skuLoading ? <CircularProgress size={18} /> : null } }}
            />
            {skuResult?.found && skuResult.asset && (
              <Box
                onClick={() => {
                  onSelectSkuAsset(skuResult.asset!, skuQuery.trim());
                  onClose();
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2.5, bgcolor: 'grey.50', cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
              >
                <Avatar variant="rounded" src={getAiImageAssetUrl(skuResult.asset.thumbnailUrl || skuResult.asset.previewUrl)} sx={{ width: 72, height: 72 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{skuResult.asset.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[skuResult.asset.productName, skuResult.asset.articleNumber].filter(Boolean).join(' | ')}
                  </Typography>
                </Box>
                <AddCircleOutlineIcon color="primary" />
              </Box>
            )}
            {skuResult && !skuResult.found && (
              <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                Ingen produktbild hittades.
              </Typography>
            )}
          </Box>
        )}

        {activeTab === 'archive' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Sök i arkivet..."
              value={archiveQuery}
              onChange={(event) => setArchiveQuery(event.target.value)}
              autoFocus
              slotProps={{ input: { endAdornment: archiveLoading ? <CircularProgress size={18} /> : null } }}
            />
            {archiveError && (
              <Typography variant="body2" color="error">{archiveError}</Typography>
            )}
            {archiveItems.length > 0 && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1, maxHeight: 360, overflowY: 'auto' }}>
                {archiveItems.map((item) => {
                  const output = item.output;
                  const title = item.prompt.slice(0, 80) || output.fileName;
                  return (
                    <Box
                      key={`${item.generationId}-${output._id}`}
                      onClick={() => {
                        onSelectArchiveOutput(item);
                        onClose();
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Välj ${title}`}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onSelectArchiveOutput(item);
                          onClose();
                        }
                      }}
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        position: 'relative',
                        border: '2px solid transparent',
                        '&:hover .base-overlay, &:focus-within .base-overlay, &:focus-visible .base-overlay': { opacity: 1 },
                        '&:focus-visible': { borderColor: 'primary.main', outline: 'none' },
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        src={getAiImageAssetUrl(output.thumbnailUrl || output.mediumUrl || output.publicUrl)}
                        alt={title}
                        sx={{ width: '100%', height: 100, bgcolor: 'grey.100', borderRadius: 2 }}
                      />
                      <Box className="base-overlay" sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.45)', opacity: 0, transition: 'opacity 0.15s' }}>
                        <AddCircleOutlineIcon sx={{ color: '#fff', fontSize: 32 }} />
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }} noWrap>
                        {title}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
            {!archiveLoading && !archiveError && archiveItems.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                Inga bilder i arkivet.
              </Typography>
            )}
            {archiveHasMore && (
              <Button
                variant="text"
                disabled={archiveLoading}
                onClick={() => void loadArchive(archiveQuery, true, archiveCursor)}
                sx={{ alignSelf: 'center', textTransform: 'none' }}
              >
                Visa fler
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default BaseImagePicker;
